import React, { useEffect, useState } from 'react';
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { Link, useSearchParams } from 'react-router-dom';
import { auth } from '../firebase';
import AuthShell from './AuthShell';
import Header from './Header';
import Footer from './Footer';
import { useI18n } from '../i18n/I18nContext';
import { pickPageCopy, resetPasswordCopy } from '../i18n/pageCopy';
import '../style/Auth.css';

const ResetPassword = () => {
  const { language } = useI18n();
  const copy = pickPageCopy(language, resetPasswordCopy);
  const [searchParams] = useSearchParams();
  const actionCode = searchParams.get('oobCode') || '';
  const [accountEmail, setAccountEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [status, setStatus] = useState('checking');
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;

    const verifyLink = async () => {
      if (!actionCode) {
        if (active) {
          setError(copy.incomplete);
          setStatus('invalid');
        }
        return;
      }

      try {
        const email = await verifyPasswordResetCode(auth, actionCode);
        if (active) {
          setAccountEmail(email);
          setStatus('ready');
        }
      } catch (verificationError) {
        if (active) {
          setError(copy.expired);
          setStatus('invalid');
        }
      }
    };

    verifyLink();
    return () => {
      active = false;
    };
  }, [actionCode, copy.expired, copy.incomplete]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (password.length < 8) {
      setError(copy.short);
      return;
    }
    if (password !== confirmPassword) {
      setError(copy.mismatch);
      return;
    }

    setStatus('saving');
    try {
      await confirmPasswordReset(auth, actionCode, password);
      setStatus('complete');
    } catch (resetError) {
      setError(copy.failed);
      setStatus('invalid');
    }
  };

  return (
    <div>
      <Header />
      <AuthShell
        eyebrow={copy.eyebrow}
        title={status === 'complete' ? copy.readyTitle : copy.chooseTitle}
        description={
          status === 'complete'
            ? copy.readyBody
            : copy.chooseBody
        }
        storyTitle={copy.storyTitle}
        storyBody={copy.storyBody}
        alternateText={copy.remember}
        alternateLabel={copy.return}
        alternatePath="/login"
      >
        {status === 'checking' && (
          <div className="auth-feedback neutral" role="status">{copy.checking}</div>
        )}

        {error && <div className="auth-feedback error" role="alert">{error}</div>}

        {status === 'invalid' && (
          <Link className="auth-submit auth-submit-link" to="/login">{copy.request}</Link>
        )}

        {status === 'complete' && (
          <Link className="auth-submit auth-submit-link" to="/login">{copy.login}</Link>
        )}

        {(status === 'ready' || status === 'saving') && (
          <form className="auth-form" onSubmit={handleSubmit}>
            <p className="reset-account-email">{copy.resetting} <strong>{accountEmail}</strong></p>
            <label>
              {copy.new}
              <span className="password-field">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  minLength="8"
                  required
                  autoComplete="new-password"
                  placeholder={copy.placeholder}
                />
                <button type="button" onClick={() => setShowPassword((current) => !current)}>
                  {showPassword ? copy.hide : copy.show}
                </button>
              </span>
            </label>
            <label>
              {copy.confirm}
              <input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                minLength="8"
                required
                autoComplete="new-password"
                placeholder={copy.repeat}
              />
            </label>
            <p className="password-guidance">{copy.guidance}</p>
            <button className="auth-submit" type="submit" disabled={status === 'saving'}>
              {status === 'saving' ? copy.saving : copy.save}
            </button>
          </form>
        )}
      </AuthShell>
      <Footer />
    </div>
  );
};

export default ResetPassword;
